import { Box, IconButton, Typography } from "@material-ui/core";
import React, { useEffect } from "react";

import LoadingIndicator from "~/components/loadingIndicator/loadingIndicator";
import Panel from "~/components/panel/panel";
import RefreshIcon from "@material-ui/icons/Refresh";
import styled from "styled-components";
import { useGetReportCommentaryMutation } from "~/store/mystore/amc.redux";
import { useTranslation } from "react-i18next";

interface AMCReportCommentaryProps {
  mid: string;
  reportRunId: string;
}

const Wrapper = styled.div`
  padding: ${({ theme }) => theme.spacing(2)}px;
  h1 {
    font-size: ${({ theme }) => theme.typography.h1.fontSize};
    font-weight: ${({ theme }) => theme.typography.h1.fontWeight};
    margin-bottom: ${({ theme }) => theme.spacing(2)}px;
  }
  h2 {
    font-size: ${({ theme }) => theme.typography.h2.fontSize};
    font-weight: ${({ theme }) => theme.typography.h2.fontWeight};
    margin-bottom: ${({ theme }) => theme.spacing(1)}px;
  }
  h3 {
    font-size: ${({ theme }) => theme.typography.h3.fontSize};
    font-weight: ${({ theme }) => theme.typography.h3.fontWeight};
    margin-bottom: ${({ theme }) => theme.spacing(1)}px;
  }
  p {
    font-size: ${({ theme }) => theme.typography.body2.fontSize};
    font-weight: ${({ theme }) => theme.typography.body2.fontWeight};
  }
  ul {
    list-style-type: disc;
    margin-left: ${({ theme }) => theme.spacing(1.25)}px;
    margin-bottom: ${({ theme }) => theme.spacing(1)}px;
    margin-top: ${({ theme }) => theme.spacing(1)}px;
    & > *:first-child {
      margin-top: 0;
    }
    li {
      font-size: ${({ theme }) => theme.typography.body2.fontSize};
      line-height: ${({ theme }) => theme.typography.body2.lineHeight};
      margin-bottom: ${({ theme }) => theme.spacing(1)}px;
      margin-top: ${({ theme }) => theme.spacing(1)}px;
    }
  }
`;

const RefreshButton = styled(IconButton)`
  margin-right: 10px;
`;

const LoadingWrapper = styled(Box)`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100px;
`;

const MAX_COMMENTARY_INDEX = 2;

const AMCReportCommentary: React.FC<AMCReportCommentaryProps> = ({
  mid,
  reportRunId,
}) => {
  const { t } = useTranslation();
  const [refreshedCommentary, setRefreshedCommentary] = React.useState<
    string | null
  >(null);
  const [currentChoice, setCurrentChoice] = React.useState<
    number | undefined
  >();

  const [getReportCommentary, { isLoading: isFetching }] =
    useGetReportCommentaryMutation();

  const fetchCommentary = async () => {
    const { commentary, index } = await getReportCommentary({
      mid,
      reportRunId,
      currentChoice,
    }).unwrap();
    commentary && setRefreshedCommentary(commentary);
    setCurrentChoice(index);
  };

  useEffect(() => {
    fetchCommentary();
  }, [getReportCommentary, mid, reportRunId]);

  return (
    <Panel
      id="widget-amc-report-commentary"
      title={t("amcWidget.commentary.title")}
      actions={
        currentChoice === 0 ||
        (currentChoice && currentChoice < MAX_COMMENTARY_INDEX) ? (
          <RefreshButton
            size="small"
            disabled={isFetching}
            onClick={fetchCommentary}
          >
            <RefreshIcon />
          </RefreshButton>
        ) : undefined
      }
      content={
        isFetching ? (
          <LoadingWrapper>
            <LoadingIndicator size={40} />
          </LoadingWrapper>
        ) : refreshedCommentary?.length ? (
          <>
            <Wrapper
              dangerouslySetInnerHTML={{
                __html: refreshedCommentary,
              }}
            />
          </>
        ) : (
          <Wrapper>
            <Typography>{t("amcWidget.commentary.noDataMsg")}</Typography>
          </Wrapper>
        )
      }
    />
  );
};

export default AMCReportCommentary;
